import { EditorContent, useEditor } from '@tiptap/react'
import { AlignType, PaddingType } from 'common/types/styleTypes'
import React, { useCallback, useEffect } from 'react'
import { Toolbar } from './components/toolbar'
import { EditorExtensionEnum } from './enums/editor-extensions-enum'
import { TiptapEditorUi } from './ui/tiptap-editor-ui'
import { TiptapWrapperUi, TiptapWrapperUiProps } from './ui/tiptap-wrapper-ui'
import { getExtensions } from './utils/get-extensions'
import { isDraftContent } from './utils/is-draft-content'
import { toggleParentDraggableAttribute } from './utils/toogle-parent-draggable-attribute'

export interface TiptapEditorUpdatePayload {
  content?: string
  fontSize?: number
  mobileFontSize?: number
  lineHeight?: number
  mobileLineHeight?: number
}

export interface TiptapEditorProps {
  wrapperStyles?: TiptapWrapperUiProps['styles']
  extensions?: EditorExtensionEnum[]
  update: (payload: Partial<TiptapEditorUpdatePayload>) => void
  isEditing: boolean
  content: string
  linkColor?: string
  textColor?: string
  padding?: PaddingType
  mobilePadding?: PaddingType
  editorTextAlign?: AlignType
  textAlign: AlignType
  fontSize?: number
  mobileFontSize?: number
  fontWeight?: string
  mobileFontWeight?: string
  enableHeadings?: boolean
  attrId: string
  backgroundColor?: string
  mobileBackgroundColor?: string
  letterSpacing?: number
  mobileLetterSpacing?: number
  fontStyle?: string
  mobileFontStyle?: string
  lineHeight?: number
  mobileLineHeight?: number
  fontFamily?: string
  mobileFontFamily?: string
  isBlogPage?: boolean
  iconClassName?: string
  isBulletList?: boolean
}

export const TiptapEditor = ({
  wrapperStyles,
  update,
  isEditing,
  content = '',
  attrId,
  linkColor,
  textColor,
  fontSize,
  mobileFontSize,
  fontWeight,
  mobileFontWeight,
  padding,
  mobilePadding,
  textAlign,
  editorTextAlign,
  enableHeadings = false,
  backgroundColor,
  mobileBackgroundColor,
  letterSpacing,
  mobileLetterSpacing,
  lineHeight,
  mobileLineHeight,
  fontFamily,
  mobileFontFamily,
  fontStyle,
  mobileFontStyle,
  isBlogPage = false,
  iconClassName = '',
  isBulletList = false,
}: TiptapEditorProps) => {
  const editor = useEditor({
    extensions: getExtensions(enableHeadings, isBulletList),
    content: isDraftContent(content),
    editorProps: {
      attributes: {
        style: `text-align: ${editorTextAlign || 'left'};`,
        iconClassName,
      },
    },
  })

  const focusEditor = useCallback(() => {
    editor?.commands.focus()
    toggleParentDraggableAttribute(false)
  }, [editor])

  useEffect(() => {
    if (!isEditing) {
      toggleParentDraggableAttribute(true)
    }
  }, [isEditing])

  useEffect(() => {
    if (editor) {
      editor.commands.setTextAlign(editorTextAlign as string)
    }
  }, [editorTextAlign, editor])

  const onSave = (value: string) => {
    if (isBulletList) {
      // Removes the icon attribute that was only needed for the re-renderer
      const newContent = value.replace(
        /<li([^>]*)\sicon="[^"]*"([^>]*)>/g,
        '<li$1$2>',
      )
      update({ content: newContent })
    } else {
      update({ content: value })
    }
  }

  const handleBlur = () => {
    if (editor) {
      onSave(editor.getHTML())
    }
  }

  useEffect(() => {
    if (!editor) return

    const incomingContent = isDraftContent(content)
    const lastSavedContent = isBulletList
      ? editor
          .getHTML()
          .replace(/<li([^>]*)\sicon="[^"]*"([^>]*)>/g, '<li$1$2>')
      : editor.getHTML()

    if (incomingContent !== lastSavedContent) {
      editor.commands.setContent(incomingContent, false)
      if (isBulletList) {
        editor?.commands.setCustomListIcon(iconClassName)
      }
    }
  }, [content])

  useEffect(() => {
    if (isBulletList) {
      editor?.commands.setCustomListIcon(iconClassName)
    }
  }, [iconClassName])

  if (!editor) return null

  return (
    <TiptapWrapperUi styles={wrapperStyles}>
      {isEditing && editor && (
        <Toolbar
          update={update}
          enableHeadings={enableHeadings}
          editor={editor}
          isBlogPage={isBlogPage}
        />
      )}
      <TiptapEditorUi
        id={attrId}
        onClick={focusEditor}
        onBlur={handleBlur}
        isEditing={isEditing}
        textAlign={textAlign}
        padding={padding}
        mobilePadding={mobilePadding}
        lineHeight={lineHeight}
        mobileLineHeight={mobileLineHeight}
        fontFamily={fontFamily}
        mobileFontFamily={mobileFontFamily}
        fontSize={fontSize}
        mobileFontSize={mobileFontSize}
        fontStyle={fontStyle}
        mobileFontStyle={mobileFontStyle}
        fontWeight={fontWeight}
        mobileFontWeight={mobileFontWeight}
        linkColor={linkColor}
        color={textColor}
        backgroundColor={backgroundColor}
        mobileBackgroundColor={mobileBackgroundColor}
        letterSpacing={letterSpacing}
        mobileLetterSpacing={mobileLetterSpacing}
        enableHeadings={enableHeadings}
      >
        <EditorContent editor={editor} />
      </TiptapEditorUi>
    </TiptapWrapperUi>
  )
}
